// Utils
import { readAsDataURL } from '../../../../common/files/promisedFileReader';
import getImageDataUriDimensions from './getImageDataUriDimensions';
import getImageHasTransparency from './getImageHasTransparency';

// Constants
import { MIME_TYPES } from '../../../../common/media/mimeConstants';

/*
 * Default transparency detector, based on MIME type.
 * Currently just assumes that SVGs are transparent, but split out into separate function to make logic clearer
 */
const imageTypeIsTransparent = (mimeType) => {
    switch (mimeType) {
        case MIME_TYPES.SVG:
            return true;
        default:
            return false;
    }
};

/**
 * JPEG images might need to have their orientation corrected as the web doesn't
 * respect EXIF orientation data.
 */
const getJpegImageData = async (inputFile) => {
    const imageDataUri = await readAsDataURL(inputFile);
    const result = await getImageDataUriDimensions(imageDataUri);

    return {
        imageDataUri,
        ...result,
        transparent: false,
    };
};

/**
 * PNGs and GIFs might have transparency, so this will draw the image to a canvas and check each pixel for transparency.
 */
const getImageDataWithTransparency = async (inputFile) => {
    const imageDataUri = await readAsDataURL(inputFile);
    const { size, img } = await getImageDataUriDimensions(imageDataUri, inputFile.type);
    const transparent = getImageHasTransparency(img);

    return {
        imageDataUri,
        size,
        transparent,
    };
};

/**
 * Other images just need the data uri and dimensions.
 */
const getOtherImageData = async (inputFile) => {
    const imageDataUri = await readAsDataURL(inputFile);
    const { size } = await getImageDataUriDimensions(imageDataUri, inputFile.type);
    // SVGs can be transparent so just assume there's transparency
    return {
        imageDataUri,
        size,
        transparent: imageTypeIsTransparent(inputFile.type),
    };
};

const getImageData = async (inputFile) => {
    switch (inputFile.type) {
        case MIME_TYPES.JPEG:
            return getJpegImageData(inputFile);
        case MIME_TYPES.PNG:
        case MIME_TYPES.GIF:
            return getImageDataWithTransparency(inputFile);
        default:
            return getOtherImageData(inputFile);
    }
};

export default async (inputFile) => {
    const result = await getImageData(inputFile);
    return { file: inputFile, ...result };
};
